import { apiClient } from '../lib/apiClient';

/**
 * E-mail notifications (backend src/routes/meAccount.routes.ts, src/routes/emailLinks.routes.ts):
 *   GET   /api/v1/me/email-preferences                       → { data: EmailPreferences }
 *   PATCH /api/v1/me/email-preferences { productUpdates?, taskReminders?, criticalAlerts?, timezone?, quietHours?, locale? }
 *   POST  /api/v1/me/email-preferences/resend-confirmation  → 202 (409 NOT_PENDING, 503 EMAIL_UNAVAILABLE)
 *   POST  /api/email/confirm      { token }   (public — double opt-in link)
 *   POST  /api/email/unsubscribe  { token }   (public — unsubscribe link)
 */
export type ProductUpdatesStatus = 'off' | 'pending_confirmation' | 'confirmed';

export interface EmailPreferences {
  productUpdates: boolean;
  productUpdatesStatus: ProductUpdatesStatus;
  productUpdatesConfirmedAt: string | null;
  taskReminders: boolean;
  /** Reef Guard critical tank alerts by e-mail; null = not decided yet (the Alerts page asks once). */
  criticalAlerts?: boolean | null;
  /** IANA time zone for quiet hours; null = Europe/Berlin. */
  timezone?: string | null;
  /** No alert e-mails 22:00–07:00 (sent after 07:00 instead). */
  quietHours?: boolean;
  locale: string;
  emailDeliveryAvailable: boolean;
}

export const EMAIL_PREFERENCES_QUERY_KEY = ['me', 'email-preferences'] as const;

export async function fetchEmailPreferences(): Promise<EmailPreferences> {
  const res = await apiClient.get<{ data: EmailPreferences }>('/api/v1/me/email-preferences');
  return res.data;
}

export async function updateEmailPreferences(input: {
  productUpdates?: boolean;
  taskReminders?: boolean;
  criticalAlerts?: boolean;
  timezone?: string | null;
  quietHours?: boolean;
  locale?: string;
}): Promise<EmailPreferences> {
  const res = await apiClient.patch<{ data: EmailPreferences }>('/api/v1/me/email-preferences', input);
  return res.data;
}

export async function resendConfirmationEmail(): Promise<void> {
  await apiClient.post('/api/v1/me/email-preferences/resend-confirmation');
}

export type EmailKind = 'product_updates' | 'task_reminders' | 'critical_alerts';

export async function confirmEmailSubscription(token: string): Promise<'confirmed' | 'already_confirmed'> {
  const res = await apiClient.post<{ data: { status: 'confirmed' | 'already_confirmed' } }>('/api/email/confirm', { token });
  return res.data.status;
}

export async function unsubscribeByToken(token: string): Promise<EmailKind> {
  const res = await apiClient.post<{ data: { kind: EmailKind } }>('/api/email/unsubscribe', { token });
  return res.data.kind;
}

/** The UI language as a short tag for the e-mail language ("de", "en", …). */
export function uiLocale(language: string | undefined): string {
  const short = (language ?? 'de').slice(0, 2).toLowerCase();
  return /^[a-z]{2}$/.test(short) ? short : 'de';
}

/** Time zones offered in the settings: the browser's own plus the common European / US ones. */
export function timeZoneOptions(browserZone: string | undefined): string[] {
  const common = [
    'Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Amsterdam', 'Europe/Paris', 'Europe/Madrid',
    'Europe/Rome', 'Europe/London', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Asia/Tokyo',
  ];
  return browserZone && !common.includes(browserZone) ? [browserZone, ...common] : common;
}
